import { Plugin, Command, Widget, toWidget, viewToModelPositionOutsideModelElement } from 'ckeditor5';
import { sanitizeMath, mathToHtml } from './mathMarkup';
import './math.css';

/*
 * Equations as inline widgets. The model element `mathml` holds sanitised
 * MathML, its LaTeX source, the display mode and the <math> element's style.
 *
 * General HTML Support would otherwise keep <math> as a raw custom element:
 * saved, but drawn empty while editing. This converter runs first.
 */

const ATTRIBUTES = ['mathml', 'latex', 'mathDisplay', 'mathStyle'];

/** @returns {{mathml: string, latex: string, display: string, style: string}} */
export function mathData(element) {
    return {
        mathml: element.getAttribute('mathml') || '',
        latex: element.getAttribute('latex') || '',
        display: element.getAttribute('mathDisplay') === 'block' ? 'block' : 'inline',
        style: element.getAttribute('mathStyle') || '',
    };
}

const selectedMath = (selection) => {
    const element = selection.getSelectedElement();
    return element && element.is('element', 'mathml') ? element : null;
};

/**
 * insertMath: inserts an equation, or replaces the selected one.
 *   execute({mathml, latex, display}) | execute({remove: true})
 * value: the selected equation's data, or null.
 */
export class MathCommand extends Command {
    refresh() {
        const model = this.editor.model;
        const selection = model.document.selection;
        const selected = selectedMath(selection);
        this.value = selected ? mathData(selected) : null;
        this.isEnabled = !!selected
            || (model.canEditAt(selection) && model.schema.checkChild(selection.getFirstPosition(), 'mathml'));
    }

    execute({ mathml, latex = '', display = 'inline', remove = false } = {}) {
        const model = this.editor.model;
        const selected = selectedMath(model.document.selection);
        model.change((writer) => {
            if (remove) {
                if (selected) writer.remove(selected);
                return;
            }
            const attributes = { mathml, latex, mathDisplay: display };
            if (selected) {
                // mathStyle (e.g. Word's font size) is kept.
                writer.setAttributes(attributes, selected);
                writer.setSelection(selected, 'on');
                return;
            }
            const element = writer.createElement('mathml', attributes);
            model.insertContent(element);
            writer.setSelection(element, 'after');
        });
    }
}

export default class MathEditing extends Plugin {
    static get pluginName() {
        return 'MathEditing';
    }

    static get requires() {
        return [Widget];
    }

    init() {
        const editor = this.editor;
        const { conversion } = editor;

        editor.model.schema.register('mathml', {
            inheritAllFrom: '$inlineObject',
            allowAttributes: ATTRIBUTES,
        });

        conversion.for('upcast').elementToElement({
            view: 'math',
            model: (viewElement, { writer, consumable }) => {
                const dom = editor.data.htmlProcessor.domConverter.viewToDom(viewElement);
                const data = sanitizeMath(dom);
                if (!data) return null;
                for (const { item } of editor.editing.view.createRangeIn(viewElement)) {
                    consumable.consume(item, { name: true });
                }
                const attributes = { mathml: data.mathml, latex: data.latex, mathDisplay: data.display };
                if (data.style) attributes.mathStyle = data.style;
                return writer.createElement('mathml', attributes);
            },
            // Ahead of General HTML Support's catch-all custom element converter.
            converterPriority: 'high',
        });

        conversion.for('dataDowncast').elementToElement({
            model: { name: 'mathml', attributes: ATTRIBUTES },
            view: (modelElement, { writer }) => {
                const data = mathData(modelElement);
                const attributes = {};
                if (data.display === 'block') attributes.display = 'block';
                if (data.latex) attributes['data-latex'] = data.latex;
                if (data.style) attributes.style = data.style;
                return writer.createRawElement('math', attributes, (domElement, domConverter) => {
                    domConverter.setContentOf(domElement, data.mathml);
                });
            },
        });

        conversion.for('editingDowncast').elementToElement({
            model: { name: 'mathml', attributes: ATTRIBUTES },
            view: (modelElement, { writer }) => {
                const data = mathData(modelElement);
                // setContentOf parses HTML, so <math> lands in the MathML namespace and renders.
                const render = writer.createRawElement('span', { class: 'ck-math__render' }, (domElement, domConverter) => {
                    domConverter.setContentOf(domElement, mathToHtml(data));
                });
                const container = writer.createContainerElement(
                    'span',
                    { class: data.display === 'block' ? 'ck-math ck-math_block' : 'ck-math' },
                    [render],
                );
                return toWidget(container, writer, { label: editor.locale.t('equation') });
            },
        });

        editor.editing.mapper.on(
            'viewToModelPosition',
            viewToModelPositionOutsideModelElement(editor.model, (viewElement) => viewElement.hasClass('ck-math')),
        );

        editor.commands.add('insertMath', new MathCommand(editor));
    }
}
